import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, UrlTree } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

import { AuthService, LOGIN_TYPE } from '../services/auth.service';
import { UserService } from '../services/user.service';
import { TeamsLoginService } from '../services/teams-login.service';
import { ZoomLoginService } from '../services/zoom-login.service';
import { ThirdPartyLoginService } from '../services/third-party-login.service';
import { ThirdPartySsoBootstrapResult } from '../services/third-party-sso.types';
import { TelemetryService } from '../services/telemetry.service';

/** Bootstrap-only entry for embedded-app SSO before handing off to `/token-login` with a token. */
@Injectable({
  providedIn: 'root',
})
export class ThirdPartySsoGuard implements CanActivate {
  private readonly bootstrapByLoginType: Partial<
    Record<LOGIN_TYPE, () => Promise<ThirdPartySsoBootstrapResult>>
  > = {
    [LOGIN_TYPE.Teams]: () => this.teamsLoginService.bootstrapTeamsSso(),
    [LOGIN_TYPE.Zoom]: () => this.zoomLoginService.bootstrapZoomSso(),
  };

  constructor(
    private readonly userService: UserService,
    private readonly teamsLoginService: TeamsLoginService,
    private readonly zoomLoginService: ZoomLoginService,
    private readonly thirdPartyLoginService: ThirdPartyLoginService,
    private readonly authService: AuthService,
    private readonly translate: TranslateService,
    private readonly telemetry: TelemetryService,
    private readonly router: Router,
  ) {}

  async canActivate(route: ActivatedRouteSnapshot): Promise<UrlTree> {
    this.userService.setLoginFlowLoading(true);
    const loginType = route.queryParams.loginType as LOGIN_TYPE;

    const bootstrap = this.bootstrapByLoginType[loginType];
    if (bootstrap) {
      try {
        const authResult = await bootstrap();
        return this.router.createUrlTree(['/token-login'], {
          queryParams: {
            loginType,
            token: authResult.firebaseCustomToken,
            continueUrl: authResult.continueUrl,
          },
        });
      } catch (error: any) {
        await this.thirdPartyLoginService.signOutForAPILogin();
        const message =
          error?.error?.err ||
          error?.message ||
          this.translate.instant('Sorry! We were unable to sign you in. Please try again.');
        this.thirdPartyLoginService.reportThirdPartySignInFailure(message, error);
        return this.router.createUrlTree(['/account/login'], {
          queryParams: { continueUrl: this.authService.getRedirectUrl() },
        });
      }
    }

    if (loginType) {
      this.telemetry.event('unsupported_third_party_sso_login_type', { login_type: loginType });
    }

    return this.router.createUrlTree(['/account/login']);
  }
}
